import { isAiAllowanceStopSummary, type AgentCard } from "@opencloud/agent-protocol";
import { Spinner } from "@opencloud/ui";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import { activeRunCard } from "../card-presentation.js";
import { latestActiveRoot, runFailurePresentation } from "../presentation.js";
import type { ConversationOutcome, ChatSnapshot as ConversationSnapshot } from "../types.js";
import { AgentCardRenderer } from "./AgentCardRenderer.js";
import { latestConversationPlan } from "../task-presentation.js";
import { chatFeedback } from '../chat-feedback.js';
import { ChatFeedback } from './ChatFeedback.js';

export function RunActivity({
  snapshot,
  attachedOutcome,
  onAnswer,
  onRetry,
  readOnly = false,
  personalAiAccounts = true,
}: {
  snapshot: ConversationSnapshot;
  liveText: string;
  attachedOutcome: ConversationOutcome | null;
  onAnswer: (answer: string) => void;
  onRetry: (runId: string) => void;
  readOnly?: boolean;
  /** False where only organisation admins connect AI accounts. */
  personalAiAccounts?: boolean;
}) {
  const root = useMemo(() => latestActiveRoot(snapshot.runs), [snapshot.runs]);
  const [mobileCollapsed, setMobileCollapsed] = useState(false);
  const slotRef = useRef<HTMLDivElement>(null);
  const visibleCardId = root?.id ?? attachedOutcome?.runId ?? null;
  const taskList = latestConversationPlan(snapshot)?.taskList;
  const showTaskList = Boolean(
    taskList &&
    root &&
    !snapshot.question &&
    !["paused", "waiting_for_user", "cancelling"].includes(root.status),
  );
  const collapsed = mobileCollapsed;

  useEffect(() => setMobileCollapsed(false), [visibleCardId]);
  useAvailableCardHeight(slotRef, collapsed, visibleCardId, true);

  if (!root && !attachedOutcome) {
    return null;
  }

  if (attachedOutcome && !root) {
    const terminalRoot =
      snapshot.runs.find(({ id }) => id === attachedOutcome.runId) ?? null;
    if (taskList && !isAiAllowanceStopSummary(attachedOutcome.summary)) return null;
    const card = outcomeCard(attachedOutcome, terminalRoot);
    return (
      <div
        className="a3-agent-card-slot"
        ref={slotRef}
        data-collapsed={collapsed}
        aria-live="polite"
      >
        <fieldset disabled={readOnly} className="m-0 min-w-0 border-0 p-0"><AgentCardRenderer
          card={card}
          collapsible
          collapsed={collapsed}
          onToggleCollapse={() => setMobileCollapsed((value) => !value)}
          onAction={(action) => {
            if (!readOnly && action.action === "retry_saved_work")
              onRetry(attachedOutcome.runId);
          }}
        /></fieldset>
      </div>
    );
  }

  if (!root) return null;
  const card = activeRunCard(snapshot, root, personalAiAccounts);
  if (showTaskList && taskList) return null;
  if (!card) return null;
  const question =
    snapshot.question?.runId === root.id ? snapshot.question : null;

  return (
    <div
      className="a3-agent-card-slot"
      ref={slotRef}
      data-collapsed={collapsed}
      aria-live="polite"
    >
      <fieldset disabled={readOnly} className="m-0 min-w-0 border-0 p-0"><AgentCardRenderer
        card={card}
        collapsible
        collapsed={collapsed}
        onToggleCollapse={() => setMobileCollapsed((value) => !value)}
        onChoice={(_sectionId, optionId) => {
          if (readOnly) return;
          const option = question?.options.find(({ id }) => id === optionId);
          if (option) {
            onAnswer(option.response);
            return;
          }
          if (optionId === "ask-for-help") {
            onAnswer("Please help me decide by explaining the tradeoffs.");
          }
        }}
        onCustomAnswer={(answer) => { if (!readOnly) onAnswer(answer); }}
        onAction={(action) => {
          if (readOnly || action.action !== "answer_question") return;
          const option = question?.options.find(({ id }) => id === action.id);
          if (option) onAnswer(option.response);
        }}
      /></fieldset>
    </div>
  );
}

export function InlineRunStatus({
  snapshot,
  liveText,
  pending = false,
}: {
  snapshot: ConversationSnapshot;
  liveText: string;
  pending?: boolean;
}) {
  const root = latestActiveRoot(snapshot.runs);
  if (root?.status === 'cancelling') return <ChatFeedback value={chatFeedback(snapshot)} />;
  const streaming = Boolean(
    root && root.status === "running" && (
      liveText.trim() || snapshot.uiMessages?.some((message) =>
        message.role === "assistant" && message.metadata.runId === root.id &&
        message.parts.some((part) => part.type === "text" && part.state === "streaming"),
      )
    ),
  );
  if (streaming) return null;
  const taskList = root ? latestConversationPlan(snapshot)?.taskList : undefined;
  const showTaskList = Boolean(
    taskList &&
      root &&
      !snapshot.question &&
      !["paused", "waiting_for_user", "cancelling"].includes(root.status),
  );
  if (pending && !liveText.trim()) {
    return <ChatFeedback value={chatFeedback(snapshot, { pending: true })} />;
  }
  if (showTaskList) return null;
  if (root && activeRunCard(snapshot, root)) return null;
  if (!root && !pending) return null;
  if (liveText.trim()) return null;
  const starting =
    !root ||
    root.status === "queued" ||
    root.status === "provisioning" ||
    root.phase === "starting";
  if (starting) {
    const latestTool = [...(snapshot.toolCalls ?? [])]
      .filter((call) => call.runId === root?.id)
      .sort((left, right) => right.sequence - left.sequence)[0];
    const activating = root?.status === "resuming" &&
      latestTool?.tool === "app.activate" && latestTool.status === "succeeded";
    const status = activating
      ? {
          headline: "Preparing your app workspace",
          detail: "Setting up access so the agent can build your app.",
        }
      : agentWorkingStatus(root);
    return (
      <article
        className="a3-agent-turn a3-inline-run-status"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        <span className="a3-inline-run-icon" aria-hidden="true">
          <Spinner aria-label="Starting your agent" />
        </span>
        <span className="a3-inline-run-copy">
          <strong>{status.headline}</strong>
          {root?.status === "queued" || activating ? (
            <small>{status.detail}</small>
          ) : null}
        </span>
      </article>
    );
  }

  const feedback = chatFeedback(snapshot, { pending, liveText });
  // The app transcript already owns its tool and reasoning disclosures.
  return <ChatFeedback value={feedback ? { ...feedback, details: [] } : null} />;
}

export function TypingIndicator() {
  return (
    <article
      className="a3-agent-turn a3-inline-typing"
      role="status"
      aria-label="Agent is thinking"
      aria-live="polite"
    >
      <span aria-hidden="true">
        <i />
        <i />
        <i />
      </span>
    </article>
  );
}

export function AgentWorkingIndicator({
  run,
}: {
  run: ConversationSnapshot["runs"][number] | null;
}) {
  const status = agentWorkingStatus(run);
  return (
    <div
      className="a3-agent-working"
      data-active={status.active}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <span className="a3-agent-working-dots">
        <Spinner aria-label={status.headline} />
      </span>
      <span className="a3-agent-working-copy">
        <strong>{status.headline}</strong>
        <span>{status.detail}</span>
      </span>
    </div>
  );
}

function agentWorkingStatus(run: ConversationSnapshot["runs"][number] | null): {
  headline: string;
  detail: string;
  active: boolean;
} {
  if (!run) {
    return {
      headline: "Starting your agent",
      detail: "Sending your request and getting the work ready.",
      active: true,
    };
  }
  if (run.status === "queued") {
    const reason = run.queue?.reason ?? "scheduler";
    const detail = {
      ai_capacity:
        "Agent is running as many builds at once as it can. Yours is waiting and will start automatically.",
      user_capacity:
        "Another app is using your runs at once. This will start automatically.",
      execution_user_capacity:
        "You already have as many builds running as your plan allows. This will start when one finishes.",
      execution_instance_capacity:
        "The Agent is busy with other work. This will start automatically in a moment.",
      // A message sent during work waits for that work; it does not join it.
      app_active:
        "The Agent will read your message when it finishes the work in progress. Press Stop to end that work now.",
      app_blocked: "The current work needs attention before this can begin.",
      scheduler:
        "This will start automatically as soon as it can.",
    }[reason];
    return {
      headline: reason === "app_active" ? "Your message is next" : "Your agent is queued",
      detail,
      active: true,
    };
  }
  if (run.status === "provisioning") {
    return {
      headline: "Starting your agent",
      detail: "Getting the app workspace ready.",
      active: true,
    };
  }
  if (run.status === "waiting_for_user") {
    return {
      headline: "Waiting for your reply",
      detail: "Your work is saved until you choose how to continue.",
      active: false,
    };
  }
  if (run.status === "pausing") {
    return {
      headline: "Saving your progress",
      detail: "Creating a safe place to pause.",
      active: true,
    };
  }
  if (run.status === "paused") {
    return {
      headline: "Agent paused",
      detail: "Your work is saved.",
      active: false,
    };
  }
  if (run.status === "resuming") {
    return {
      headline: "Preparing to continue",
      detail: "Picking up from the saved work.",
      active: true,
    };
  }
  if (run.status === "cancelling") {
    return {
      headline: "Stopping safely",
      detail: "Unfinished work will not be released.",
      active: true,
    };
  }
  if (run.phase === "understanding") {
    return {
      headline: "Agent is reading your request",
      detail: "Working out what to build first.",
      active: true,
    };
  }
  return {
    headline: "Starting your agent",
    detail: "Reading your request and preparing the first steps.",
    active: true,
  };
}

function useAvailableCardHeight(
  slotRef: RefObject<HTMLDivElement | null>,
  collapsed: boolean,
  cardId: string | null,
  enabled: boolean,
): void {
  useLayoutEffect(() => {
    const slot = slotRef.current;
    if (!enabled || !slot || collapsed || !cardId) return;
    const footer = slot.closest<HTMLElement>(".a3-composer-footer");
    const workspace = slot.closest<HTMLElement>(".a3-workspace");
    const header = workspace?.querySelector<HTMLElement>(".a3-header");
    const composer = footer?.querySelector<HTMLElement>(".a3-composer-region");
    if (!footer || !workspace || !header || !composer) return;

    const update = () => {
      const viewport = window.visualViewport;
      const viewportTop = viewport?.offsetTop ?? 0;
      const viewportBottom =
        viewportTop + (viewport?.height ?? window.innerHeight);
      const footerStyle = window.getComputedStyle(footer);
      const padding =
        parseFloat(footerStyle.paddingTop) +
        parseFloat(footerStyle.paddingBottom);
      const gap = parseFloat(footerStyle.rowGap || footerStyle.gap || "0");
      const available =
        viewportBottom -
        Math.max(viewportTop, workspace.getBoundingClientRect().top) -
        header.getBoundingClientRect().height -
        composer.getBoundingClientRect().height -
        padding -
        gap -
        8;
      slot.style.setProperty(
        "--a3-agent-card-max-height",
        `${Math.max(88, Math.floor(available))}px`,
      );
    };

    update();
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    observer?.observe(workspace);
    observer?.observe(header);
    observer?.observe(composer);
    window.addEventListener("resize", update);
    window.visualViewport?.addEventListener("resize", update);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("resize", update);
    };
  }, [cardId, collapsed, enabled, slotRef]);
}

function outcomeCard(
  outcome: ConversationOutcome,
  run: ConversationSnapshot["runs"][number] | null,
): AgentCard {
  const failed = outcome.status === "failed";
  const failure = runFailurePresentation(run, outcome.summary);
  const canRetry = failed && (run?.checkpointVersion ?? 0) > 0;
  const verification = outcome.release
    ? [
        ...outcome.release.reviews.map((review) => ({
          id: `review:${review.area}`,
          label: review.label,
          status:
            review.status === "passed"
              ? ("passed" as const)
              : review.status === "not_needed"
                ? ("not_needed" as const)
                : ("failed" as const),
          detail: review.detail,
        })),
        ...outcome.release.gates.map((gate) => ({
          id: `gate:${gate.id}`,
          label: gate.label,
          status:
            gate.status === "passed"
              ? ("passed" as const)
              : gate.status === "not_applicable"
                ? ("not_needed" as const)
                : gate.status === "not_tested"
                  ? ("not_tested" as const)
                  : ("failed" as const),
          detail: gate.detail,
        })),
      ]
    : [];

  return {
    id: `outcome:${outcome.runId}`,
    type: failed ? "failure_card" : "stopped_card",
    revision: 1,
    state: "settled",
    icon: failed ? "alert" : "stopped",
    title: failed ? failure.eyebrow : "Stopped",
    subtitle: failed ? failure.title : isAiAllowanceStopSummary(outcome.summary) ? "AI budget reached" : "Nothing unfinished was released",
    styleColor: failed ? "red" : "orange",
    sections: [
      {
        id: "summary",
        type: "markdown",
        markdown: outcome.summary,
        display: "plain",
      },
      ...(verification.length > 0
        ? [
            {
              id: "checks",
              type: "disclosure" as const,
              label: "How this was checked",
              expandedLabel: "Hide checks",
              sections: [
                {
                  id: "verification",
                  type: "verification" as const,
                  title: "Verification",
                  checks: verification,
                },
              ],
            },
          ]
        : []),
      ...(canRetry
        ? [
            {
              id: "actions",
              type: "actions" as const,
              actions: [
                {
                  id: "retry",
                  label: "Try again from the saved work",
                  action: "retry_saved_work",
                  style: "secondary" as const,
                },
              ],
            },
          ]
        : []),
    ],
  };
}
